import {
  type DropdownOption,
  type FireflyTertiaryButtonMenuItem,
  type MenuOption,
  DateFormat,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import {
  MonthlyAllowedAppointmentTypes,
  Recurrence,
} from '~/lib/util/calendar.util';
import { capitalizeFirstLetter } from '~/lib/common';

import { toStateMenuOption } from '~/lib/util/menu';
import { getFormattedNoteActivityDateToDisplayString } from '~/lib/util/patient.util';
import type {
  ScheduledApppointmentsResponse,
  ScheduledAppointments,
} from './EditAppointment/util';

const util = {
  convertAppointmentDataToButtonMenuItem: (
    appointmentData: AppointmentDetailResponse,
  ): FireflyTertiaryButtonMenuItem => {
    const dateStr = appointmentData?.appointmentStartDateTime ?? null;
    let typeLabel = appointmentData?.appointmentTypeLabel ?? null;
    if (dateStr) {
      typeLabel = typeLabel.concat(
        `\n(${getFormattedNoteActivityDateToDisplayString(
          DateTime.fromISO(dateStr).toJSDate(),
        )})`,
      );
    }
    return {
      node: appointmentData.appointmentExternalId,
      label: typeLabel,
    };
  },
  convertAppointmentDataToMenuItem: (
    appointmentData: AppointmentDetailResponse,
  ): MenuOption => {
    const dateStr = appointmentData?.appointmentStartDateTime ?? null;
    let typeLabel = appointmentData.appointmentTypeLabel;
    if (dateStr) {
      typeLabel = typeLabel.concat(
        `\n(${getFormattedNoteActivityDateToDisplayString(
          DateTime.fromISO(dateStr).toJSDate(),
        )})`,
      );
    }
    return toStateMenuOption({
      externalId: appointmentData.appointmentExternalId,
      label: typeLabel,
    });
  },

  joinStringsWithOr: (array: string[]) => {
    if (!array.length) return '';
    if (array.length === 1) return array[0];
    const allButLast = array.slice(0, -1).join(', ');
    const lastElement = array[array.length - 1];
    return `${allButLast} or ${lastElement}`;
  },
  // response example of above function
  // joinStringsWithOr(["apple"])                   ----> "apple"
  // joinStringsWithOr(["apple", "banana"])         ----> "apple or banana"
  // joinStringsWithOr(["apple", "banana", "pear"]) ----> "apple, banana or pear"
};

export const getRecurrenceOptions = ({
  excludeMonthly = true,
}: {
  excludeMonthly?: boolean;
}) => {
  return Object.values(Recurrence)
    .filter((value) => (excludeMonthly ? value !== Recurrence.MONTHLY : true))
    .map((value) => ({
      label: value === Recurrence.MONTHLY ? 'Every 4 weeks' : value,
      value,
    }));
};

export const isMonthlyAllowedAppointmentTypes = (
  selectedAppointmentType: DropdownOption,
): boolean => {
  const monthlyAllowedAppointmentTypes = Object.values(
    MonthlyAllowedAppointmentTypes,
  );
  return monthlyAllowedAppointmentTypes.includes(
    selectedAppointmentType.label as MonthlyAllowedAppointmentTypes,
  );
};

export const formatAppointmentTimeFromDate = (date: string): string => {
  return DateTime.fromISO(date)
    .toFormat(DateFormat.timeWithoutSpace)
    .toLowerCase();
};

export const getCategorizedPatientAndSupportAppointments = (
  data?: ScheduledApppointmentsResponse[],
): ScheduledAppointments => {
  const { patientAppointments, supportAppointments }: ScheduledAppointments = {
    patientAppointments: [],
    supportAppointments: [],
  };

  const isPatientAttending = (appt: ScheduledApppointmentsResponse) =>
    ('isPatientAttending' in appt && appt.isPatientAttending) ||
    ('patientAttending' in appt && appt.patientAttending);

  const hasSupportAttendees = (appt: ScheduledApppointmentsResponse) =>
    ('supportAttendees' in appt && appt.supportAttendees?.length > 0) ||
    ('supportAttendeeExternalIds' in appt &&
      appt.supportAttendeeExternalIds?.length > 0);

  for (const appt of data ?? []) {
    if (isPatientAttending(appt)) patientAppointments.push(appt);
    if (hasSupportAttendees(appt)) supportAppointments.push(appt);
  }

  return { patientAppointments, supportAppointments };
};

export const getDifferenceInDays = (endDate?: string | DateTime): number => {
  if (!endDate) {
    return 0;
  }

  const finalDate =
    endDate instanceof DateTime ? endDate : DateTime.fromISO(endDate);

  return Math.max(0, Math.ceil(finalDate.diff(DateTime.now(), 'days').days));
};

export const hasMatchingProviderTypeName = (
  appointmentType: AppointmentType,
  providerTypeName: string,
) => appointmentType.providerTypeNames.includes(providerTypeName);

export const uniqueBy = <TItem>(
  items: TItem[],
  getKey: (item: TItem) => string | number,
): TItem[] => {
  return Array.from(
    new Map(items.map((item) => [getKey(item), item])).values(),
  );
};

export const capitalizeEachWord = (value: string) => {
  const normalizedValue = (value ?? '').trim();

  return normalizedValue
    .split(' ')
    .filter(Boolean)
    .map((word) => capitalizeFirstLetter(word))
    .join(' ');
};

export default util;
